/**
 * Pinned action footer for `ahoy_task` records: "Mark complete" (primary) +
 * "Reassign" (secondary), pinned to the bottom of the record content column.
 * Self-gates to null for every other record type or when the user lacks
 * permission. Mirrors `EmailThreadReplyFooter`'s hook-gating contract.
 *
 * Hooks run before the early-return guards (Rules of Hooks).
 */

import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import { isTaskRecord } from '../memo/noteKind';
import { extractMemoData, getMemoAssociationNames } from '../memo/notes.utils';
import { OrgMemberPicker } from './OrgMemberPicker';
import { TASK_STAGE_ASSOCIATION_TYPE_NAME } from './task.constants';
import { useTaskAssigneeMutation } from './TaskAssigneeMutationContext';
import { useCompletedTaskStage } from './useCompletedTaskStage';

export function TaskActionFooter({
  object,
}: {
  object: CrmObject;
}): ReactElement | null {
  const isTask = isTaskRecord(object);
  const entityType = 'ahoy_task';
  const { owner: ownerAssociationTypeName } =
    getMemoAssociationNames(entityType);
  // The task family always has a real stage association name (unlike notes,
  // which have none — epic #13640).
  const stageAssociationTypeName = TASK_STAGE_ASSOCIATION_TYPE_NAME;

  // Hooks must run before any early return (Rules of Hooks).
  // MAINTENANCE: Every fetch-bearing hook added here MUST include `enabled:
  // isTask` (or an equivalent guard). RecordBody renders this footer
  // unconditionally for all CRM objects; without the guard a new hook would
  // fire on contacts, companies, plain notes, etc.
  const { user } = useAuthInfo();
  const userId = user?.id ? String(user.id) : undefined;

  const { hasPermission } = usePermissions();
  const canAct = hasPermission(entityType, 'ahoy_association_added');

  const task = useMemo(
    () =>
      extractMemoData(
        object,
        ownerAssociationTypeName,
        stageAssociationTypeName
      ),
    [object, ownerAssociationTypeName, stageAssociationTypeName]
  );

  const {
    completedStageEntityId,
    isError: completedStageError,
    refetch: refetchCompletedStage,
  } = useCompletedTaskStage(object.org_id, task.stage_entity_id, isTask);

  const updateAssociation = useTaskAssigneeMutation();

  // The stage mutation deliberately does NOT expose `isPending` — track
  // in-flight locally to prevent double-submit.
  const [isCompleting, setIsCompleting] = useState(false);

  const handleMarkComplete = useCallback(() => {
    // When the completed stage couldn't be resolved because its pipeline fetch
    // FAILED (not merely slow), the primary button becomes a retry: re-run the
    // fetch instead of leaving the user with a permanently dead "Mark complete".
    if (completedStageEntityId == null) {
      if (completedStageError) {
        refetchCompletedStage();
      }
      return;
    }
    if (
      !userId ||
      isCompleting ||
      task.stage_entity_id === completedStageEntityId
    ) {
      return;
    }
    setIsCompleting(true);
    const priorStageEntityId = task.stage_entity_id;
    submitEventsOptimistic({
      orgId: object.org_id,
      userId,
      entityTypeName: object.entity_type_name,
      primaryEntityId: object.entity_id,
      fallbackSeq: object.last_event_seq,
      singleCardinalityReplacements: priorStageEntityId
        ? [
            {
              entityId: object.entity_id,
              associationTypeName: stageAssociationTypeName,
              priorTargetEntityId: priorStageEntityId,
              newTargetEntityId: completedStageEntityId,
            },
          ]
        : undefined,
      build: builder => {
        builder.associationAdded(
          object.entity_id,
          object.entity_type_name,
          stageAssociationTypeName,
          completedStageEntityId
        );
      },
    })
      .finally(() => setIsCompleting(false))
      .catch(err => {
        toast.error("We couldn't update that status. Try again.");
        logger.error(
          'TaskActionFooter: mark complete failed',
          err instanceof Error ? err : new Error(String(err)),
          {
            org_id: object.org_id,
            entity_id: object.entity_id,
            target_stage: completedStageEntityId,
          }
        );
      });
  }, [
    userId,
    completedStageEntityId,
    completedStageError,
    refetchCompletedStage,
    isCompleting,
    task.stage_entity_id,
    object,
    stageAssociationTypeName,
  ]);

  const handleReassign = useCallback(
    (entityId: string) => {
      updateAssociation
        .mutateAsync({
          object,
          associationTypeName: ownerAssociationTypeName,
          targetIds: [entityId],
        })
        .catch(err => {
          toast.error('Reassignment failed. Please try again.');
          logger.error(
            'TaskActionFooter: reassign failed',
            err instanceof Error ? err : new Error(String(err)),
            {
              org_id: object.org_id,
              entity_id: object.entity_id,
              target_owner: entityId,
            }
          );
        });
    },
    [updateAssociation, object, ownerAssociationTypeName]
  );

  // Gate rendering after all hooks have run.
  if (!isTask || !canAct || !userId) {
    return null;
  }

  const alreadyComplete =
    completedStageEntityId != null &&
    task.stage_entity_id === completedStageEntityId;

  return (
    <div className='shrink-0 px-4 pb-3' data-component='task-action-footer'>
      <div className='flex flex-col gap-2 sm:flex-row sm:justify-end'>
        {!alreadyComplete && (
          <Button
            className='w-full sm:w-auto'
            color='primary'
            onClick={handleMarkComplete}
            // Disabled ONLY while genuinely loading (or a completion is in
            // flight). On a fetch error the button stays enabled and acts as a
            // retry — a failure must not masquerade as a permanent loading
            // state that dead-ends "Mark complete".
            isDisabled={
              (completedStageEntityId == null && !completedStageError) ||
              isCompleting
            }
            disabledTooltip={
              completedStageEntityId == null && !completedStageError
                ? 'Loading statuses…'
                : undefined
            }
          >
            {completedStageEntityId == null && completedStageError
              ? 'Retry'
              : 'Mark complete'}
          </Button>
        )}
        <OrgMemberPicker
          orgId={object.org_id}
          selectedEntityId={task.owner_entity_id}
          onSelect={handleReassign}
          enabled={isTask}
        >
          <Button
            className='w-full sm:w-auto'
            color='secondary'
            isDisabled={updateAssociation.isPending}
          >
            Reassign
          </Button>
        </OrgMemberPicker>
      </div>
    </div>
  );
}
